import { useState } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { Phone, Trash } from "@phosphor-icons/react";

export const LeadCallLog = ({ lead, onUpdate }) => {
  const [text, setText] = useState("");
  const [remindAt, setRemindAt] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = [...(lead.call_notes || [])].reverse();

  const addNote = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const { data } = await client.post(`/admin/contact-requests/${lead.id}/call-notes`, {
        text,
        remind_at: remindAt || null,
      });
      onUpdate?.({
        call_notes: [...(lead.call_notes || []), data.note],
        ...(data.next_followup_at ? { next_followup_at: data.next_followup_at } : {}),
      });
      setText("");
      setRemindAt("");
      toast.success(data.next_followup_at ? `Note saved — reminder ${data.next_followup_at}` : "Note saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await client.delete(`/admin/contact-requests/${lead.id}/call-notes/${noteId}`);
      onUpdate?.({ call_notes: (lead.call_notes || []).filter((n) => n.id !== noteId) });
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mb-6" data-testid="lead-call-log">
      <div className="overline mb-2 flex items-center gap-1.5">
        <Phone size={12} weight="bold" /> CALL LOG
      </div>
      <div className="space-y-2">
        <textarea
          data-testid="call-note-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Jot what you talked about, objections, next steps..."
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-neutral-200 leading-relaxed focus:outline-none focus:border-[#FF5A1F]/60 resize-y"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-[11px] text-neutral-500 inline-flex items-center gap-1.5">
            Remind me
            <input
              data-testid="call-note-remind-input"
              type="date"
              value={remindAt}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setRemindAt(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-neutral-200 focus:outline-none focus:border-[#FF5A1F]/60 [color-scheme:dark]"
            />
          </label>
          <button
            data-testid="call-note-save-btn"
            onClick={addNote}
            disabled={saving || !text.trim()}
            className={`rounded-full px-4 py-2 text-xs border border-white/15 text-neutral-300 hover:border-white/30 hover:text-white transition-colors ${
              saving || !text.trim() ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {saving ? "Saving..." : "Add note"}
          </button>
        </div>
      </div>
      {notes.length > 0 && (
        <div className="mt-3 space-y-2">
          {notes.map((n) => (
            <div
              key={n.id}
              data-testid={`call-note-${n.id}`}
              className="group flex items-start gap-3 rounded-md bg-white/[0.03] border border-white/5 px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-neutral-200 whitespace-pre-wrap break-words">{n.text}</div>
                <div className="text-[10px] text-neutral-500 font-mono mt-1">
                  {new Date(n.created_at).toLocaleString()}
                  {n.remind_at && <span className="text-amber-400/80"> · reminds {n.remind_at}</span>}
                </div>
              </div>
              <button
                data-testid={`call-note-delete-${n.id}`}
                onClick={() => deleteNote(n.id)}
                title="Delete note"
                className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-red-400 transition-all shrink-0 mt-0.5"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
